/**
 * Interactive Iframe Utility
 * Bidirectional communication management between parent page and iframe
 * Modern version migrated from front/src/interactive-iframe.js
 */

/**
 * Message handler from iframe
 * @param {HTMLIFrameElement} iframeElement - Target iframe element
 * @returns {Function} Handler for message events
 */
const handleMessage = (iframeElement) =>
  /**
   * Message event handler
   * @param {MessageEvent} event - Received message event
   */
  (event) => {
  // Verify that message comes from target iframe
  if (event.source !== iframeElement.contentWindow) {
    return;
  }

  if (!event.data) {
    // Silently ignore messages without payload
    return;
  }

  const { data: payload } = event;

  // Adjust iframe height if provided
  if (payload.height && payload.height !== iframeElement.style.height) {
    iframeElement.style.height = `${payload.height}px`;
  }

  // Synchronize parent URL with iframe navigation
  if (payload.location?.pathname) {
    const hash = globalThis.location.hash.slice(1);
    const currentState = new URLSearchParams(hash);

    currentState.set('pathname', payload.location.pathname);
    globalThis.location.hash = `#${currentState.toString()}`;
  }
};

/**
 * Calcule les paddings verticaux des éléments fixes (header, footer)
 * @param {Object} selectors - Sélecteurs CSS des éléments fixes
 * @param {string} selectors.top - Sélecteur pour l'élément en haut
 * @param {string} selectors.bottom - Sélecteur pour l'élément en bas
 * @returns {Object} Dimensions des paddings
 */
const getVerticalPadding = ({ top, bottom } = {}) => {
  const output = { top: 0, bottom: 0 };

  if (top) {
    const topElement = document.querySelector(top);
    output.top = topElement?.getBoundingClientRect()?.bottom || 0;
  }

  if (bottom) {
    const bottomElement = document.querySelector(bottom);
    output.bottom = bottomElement?.getBoundingClientRect()?.top || 0;
  }

  output.sum = output.top + output.bottom;

  return output;
};

/**
 * Configure et initialise la communication interactive avec un iframe
 * @param {Object} config - Configuration de l'iframe
 * @param {string} config.basePath - URL de base de l'application dans l'iframe
 * @param {string} config.iframeSelector - Sélecteur CSS de l'iframe
 * @param {HTMLIFrameElement} config.iframeElement - Élément iframe (optionnel)
 * @param {Object} config.verticalPaddingSelectors - Sélecteurs des éléments fixes
 * @returns {Function|undefined} Fonction de nettoyage optionnelle
 */
const setupInteractiveIframe = ({
  basePath = '',
  iframeSelector = 'iframe',
  iframeElement = document.querySelector(iframeSelector),
  verticalPaddingSelectors = {},
}) => {
  // Compatibility checks
  if (!iframeElement) {
    // Silent failure if iframe is not found
    return null;
  }

  if (typeof globalThis === 'undefined') {
    // Silent failure if globalThis is not available
    return null;
  }

  // Initialize iframe URL from parent URL hash
  const initializeIframeSrc = () => {
    const hash = globalThis.location.hash.slice(1);
    const currentState = new URLSearchParams(hash);
    const pathName = currentState.get('pathname') || '';
    const location = `${basePath}${pathName}`;

    if (pathName && iframeElement.src !== location) {
      iframeElement.src = location;
    }
  };

  // Scroll event handler with iframe propagation
  const handleScroll = () => {
    const { top } = iframeElement.getBoundingClientRect();
    const verticalOffset = getVerticalPadding(verticalPaddingSelectors).top;
    const value = (top < verticalOffset) ? (top * -1 + verticalOffset) : 0;

    iframeElement.contentWindow?.postMessage(
      { type: 'scrollEvent', value },
      basePath,
    );
  };

  // Resize event handler with iframe propagation
  const handleResize = () => {
    iframeElement.contentWindow?.postMessage(
      {
        type: 'resizeEvent',
        vh: globalThis.innerHeight,
        vw: globalThis.innerWidth,
        maskedHeight: getVerticalPadding(verticalPaddingSelectors).sum,
      },
      basePath,
    );
  };

  // Initial configuration
  initializeIframeSrc();

  // Listen to iframe messages
  globalThis.addEventListener('message', handleMessage(iframeElement));

  // Scroll handling with throttling
  let scrollTimeout;
  const throttledScrollHandler = () => {
    if (scrollTimeout) return;
    scrollTimeout = setTimeout(() => {
      handleScroll();
      scrollTimeout = null;
    }, 16); // ~60fps
  };

  // Gestion du resize avec throttling
  let resizeTimeout;
  const throttledResizeHandler = () => {
    if (resizeTimeout) return;
    resizeTimeout = setTimeout(() => {
      handleResize();
      resizeTimeout = null;
    }, 100);
  };

  // Initialisation immédiate
  handleScroll();
  handleResize();

  // Event listeners
  document.addEventListener('scroll', throttledScrollHandler, { passive: true });
  globalThis.addEventListener('resize', throttledResizeHandler);

  // Safety interval for cases where events don't trigger
  const intervalId = setInterval(() => {
    handleScroll();
    handleResize();
  }, 1000);

  // Fonction de nettoyage (optionnelle pour usage avancé)
  return () => {
    document.removeEventListener('scroll', throttledScrollHandler);
    globalThis.removeEventListener('resize', throttledResizeHandler);
    globalThis.removeEventListener('message', handleMessage(iframeElement));
    clearInterval(intervalId);
  };
};

export default setupInteractiveIframe;
